import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertTriangle, Download, FileText } from "lucide-react";

export default function DocumentsPage() {
  const { t } = useI18n();
  const [docs, setDocs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [emps, setEmps] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const [d, a, e] = await Promise.all([
        api.get("/documents"), api.get("/alerts/expiring?days=90"), api.get("/employees")
      ]);
      setDocs(d.data); setAlerts(a.data);
      const m = {}; e.data.forEach(x => { m[x.id] = x; });
      setEmps(m);
    })();
  }, []);

  const downloadDoc = (did) => {
    const token = localStorage.getItem("logihr_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${did}/download?auth=${token}`, "_blank");
  };

  const filtered = docs.filter(d => {
    if (!q) return true;
    const emp = emps[d.employee_id];
    const text = `${d.doc_name} ${d.doc_type} ${emp?.first_name} ${emp?.last_name} ${emp?.employee_no}`.toLowerCase();
    return text.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-5" data-testid="documents-page">
      <Card className="p-5 border-border/60">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> {t("expiringDocs")}
          </h3>
          <Badge variant="secondary">{alerts.length}</Badge>
        </div>
        {alerts.length === 0 ? <div className="text-sm text-muted-foreground">{t("noUpcoming")}</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {alerts.map((a, i) => (
              <Link key={i} to={`/employees/${a.employee_id}`} data-testid={`alert-doc-${i}`}
                    className="block p-3 rounded border border-border/60 hover:border-primary/50 hover:bg-accent transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{a.employee_name}</span>
                  <Badge variant={a.severity === "expired" || a.severity === "critical" ? "destructive" : "secondary"}>
                    {a.days_remaining < 0 ? t("expired") : `${a.days_remaining}d`}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1 font-mono-brand uppercase tracking-wider">{a.label} · {a.date}</div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 border-border/60">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="font-display font-bold">{t("documents")}</h3>
          <Input data-testid="input-doc-search" placeholder={t("search")} value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
        </div>
        {filtered.length === 0 ? <div className="py-6 text-center text-sm text-muted-foreground">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {filtered.map((d, i) => {
              const emp = emps[d.employee_id];
              return (
                <div key={d.id} className="py-3 flex items-center gap-3">
                  <FileText className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{d.doc_name}</div>
                    <div className="text-xs text-muted-foreground truncate font-mono-brand uppercase tracking-wider">
                      {d.doc_type} · {emp ? `${emp.first_name} ${emp.last_name}` : "—"} {d.expiry_date ? `· ${d.expiry_date}` : ""}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => downloadDoc(d.id)}><Download className="w-3 h-3" /></Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
